import type { FC } from 'react'
import React, { memo, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { Icon } from '@frontend/pole-ui/lib/components/Icon'
import { ShevronDownIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronDownIcon'
import { ShevronRightIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronRightIcon'
import { ShevronUpIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronUpIcon'
import classNames from 'classnames'
import { openModal } from '@/components/composed/Modal/actions'
import { Button } from '@/components/ui'
import { isAuthenticated as isUserAuthenticated, isUserAgrarian } from '@/logic/auth/reducer'
import { useGtm } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmLabel } from '@/logic/metrika/financing/types'
import { ProductInfoContext } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/components/LoanProductsList/components/LoanProduct/constants'
import { ELoanProductActionType } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/types'
import type { RootState } from '@/redux/interfaces'
import { useRouterApi } from '@/router/hooks'
import { EAgriculturalProductsRoute } from '@/types'
import { useOpenApplyingFinancingOfferModal } from '@/utils/hooks/applyingFinancingOffer/useOpenApplyingFinancingOfferModal'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { messages } from './messages'
import type { ILoanProductFooterProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  isAuthenticated: isUserAuthenticated()(state),
})

const LoanProductFooter: FC<ILoanProductFooterProps> = props => {
  const { isShowCollapsingBlock, setIsShowCollapsingBlock, isLeasing, onPushGtmEventActionOrder, onPushGtmEventActionMore } = props
  const { isMobile } = useDeviceType()

  const dispatch = useDispatch()
  const { isAuthenticated, isAgrarian } = useSelector(mapState)

  const history = useRouterApi()

  const productInfo = useContext(ProductInfoContext)

  const { productId, url, offerType, gtmId, hasConsulting } = productInfo!
  const { pushGtmProduct } = useGtm()

  const isMandarin = productId === FundingProduct.BNPL_MANDARIN

  const showCalculateButton = productId === FundingProduct.EXPRESS_MFO || productId === FundingProduct.BNPL_POLECREDIT

  const leasingUrl = isLeasing ? `${url}#form` : url

  const { openApplyingFinancingOfferModal } = useOpenApplyingFinancingOfferModal({
    offerType,
    productId,
    gtmId,
  })

  const onClickButton = () => {
    pushGtmProduct(gtmId, EGtmLabel.ACTION)

    if (isMandarin) {
      history.push(EAgriculturalProductsRoute.main)

      return
    }

    openApplyingFinancingOfferModal(gtmId)
  }

  const onConsultClick = () => {
    dispatch(
      openModal({
        options: {
          dialogId: 'ConsultingFormModal',
        },
      }),
    )
  }

  const onClickLink = () => pushGtmProduct(gtmId, EGtmLabel.BUTTON)
  const onClickInstallment = () => pushGtmProduct(gtmId, EGtmLabel.INSTALLMENT)

  const buttonText = isMandarin ? messages.installment : messages.apply

  const toogleCollapsing = () => {
    if (isShowCollapsingBlock) setIsShowCollapsingBlock(null)

    if (!isShowCollapsingBlock && productId) setIsShowCollapsingBlock(productId)
  }

  const actionsButton = () => {
    switch (productInfo?.actionType) {
      case ELoanProductActionType.INSTALLMENT_LINK:
        return (
          <CommonLink
            url={leasingUrl}
            onClick={() => {
              onClickInstallment()
              onPushGtmEventActionOrder?.()
            }}
          >
            <Button className="loanProductFooter__buttonWidth" modifiers={['green', 'full-width-always']}>
              {messages.leasingMessage}
            </Button>
          </CommonLink>
        )

      case ELoanProductActionType.COLLAPSING_BLOCK:
        return (
          <>
            <Button
              onClick={() => {
                toogleCollapsing()
                onPushGtmEventActionOrder?.()
              }}
              className={classNames('loanProductFooter__buttonWidth', { button_small: isMobile })}
              modifiers={['green', 'full-width-always']}
              iconProps={{
                component: isShowCollapsingBlock ? <ShevronUpIcon /> : <ShevronDownIcon />,
                className: 'loanProductFooter__actionsIcon',
              }}
              iconPosition="right"
            >
              {showCalculateButton ? messages.calcOnline : messages.apply}
            </Button>

            {url && (
              <CommonLink
                className={classNames('loanProductFooter__link', { 'button button_outline-gray button_small': isMobile })}
                url={url}
                onClick={() => {
                  onClickLink()
                  onPushGtmEventActionMore?.()
                }}
              >
                {messages.more}
                <Icon component={<ShevronRightIcon />} />
              </CommonLink>
            )}
          </>
        )

      default:
        return (
          <>
            <Button
              modifiers={['green', 'full-width-always']}
              onClick={() => {
                onClickButton()
                onPushGtmEventActionOrder?.()
              }}
              className="loanProductFooter__buttonWidth"
              isDisabled={!isAgrarian && isAuthenticated && !productInfo?.isAlwaysActiveButton}
            >
              {buttonText}
            </Button>

            {url && (
              <CommonLink
                className={classNames('loanProductFooter__link', { 'button button_outline-gray button_small': isMobile })}
                url={url}
                onClick={() => {
                  onClickLink()
                  onPushGtmEventActionMore?.()
                }}
              >
                {messages.more}
              </CommonLink>
            )}

            {hasConsulting && (
              <CommonLink
                className={classNames('loanProductFooter__link', { 'button button_outline-gray button_small': isMobile })}
                onClick={onConsultClick}
              >
                {messages.more}
                <Icon component={<ShevronRightIcon />} />
              </CommonLink>
            )}
          </>
        )
    }
  }

  return (
    <div className="loanProductFooter">
      <div className="loanProductFooter__actions">{actionsButton()}</div>
    </div>
  )
}

export default memo(LoanProductFooter)
